import React from "react";

import { Box, Button, Paper, Typography } from "@mui/material";

import {
  AccountBalanceRounded,
  ArrowForwardRounded,
  DescriptionRounded,
  FolderCopyRounded,
  LibraryBooksRounded,
  LockRounded,
  ManageAccounts,
  MenuBookRounded,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import { useMovimentoReduzido as useReducedMotion } from "../../contextos/PreferenciasContext";
import { useNavigate } from "react-router-dom";

import { obterTransicaoPaginaInicial } from "../../componentes/comuns/TransicaoPagina";

import {
  coresTextosGlobais, textosGlobais,
  iconesGlobais,
  espacamentosGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../estilos/global";

// ====================================================
// ESTILOS REUTILIZADOS DO PAINEL DO ADMINISTRADOR
// ====================================================

const ESTILO_CAIXA_ICONE = {
  width: 48,
  height: 48,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  borderRadius: 1.5,
  color: "var(--sear-azul, #087cf0)",
  backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
};

const ESTILO_TITULO_SECAO = {
  color: coresTextosGlobais.tituloSecao,
  ...textosGlobais.tituloSecao,
  fontSize: tamanhosPaginasAdministrativas.tituloSecao,
  overflowWrap: "anywhere",
};

const ESTILO_DESCRICAO = {
  color: "text.secondary",
  ...textosGlobais.textoPadrao,
  fontSize: tamanhosControlesGlobais.descricao,
};

const TELA_COM_ALTURA_REDUZIDA =
  "@media (min-width: 900px) and (max-height: 740px)";

// ====================================================
// CARTÕES DO PAINEL DA SUPERINTENDÊNCIA
// Rotas existentes no acesso da SRE.
// ====================================================

const ATALHOS = [
  {
    titulo: "Processos",
    descricao: "Consulte e gerencie os tipos de processos da Superintendência.",
    rota: "/admin/processo",
    Icone: FolderCopyRounded,
  },
  {
    titulo: "Documentos",
    descricao: "Consulte e gerencie os documentos utilizados nos processos.",
    rota: "/admin/cadastro-documento",
    Icone: DescriptionRounded,
  },
  {
    titulo: "Etapas / Modalidades",
    descricao: "Acesse o gerenciamento de etapas e modalidades de ensino.",
    rota: "/admin/visualizar/modalidade",
    Icone: MenuBookRounded,
  },
  {
    titulo: "Modelos e Roteiros",
    descricao: "Acesse os modelos e roteiros disponibilizados no sistema.",
    rota: "/modelos",
    Icone: LibraryBooksRounded,
  },
  {
    titulo: "Usuários",
    descricao: "Consulte os usuários e acesse o cadastro de analistas.",
    rota: "/admin/usuarios",
    Icone: ManageAccounts,
  },
];

// ====================================================
// VISÃO GERAL
// Somente estrutura visual, sem dados simulados.
// ====================================================

const RESUMOS_VISUAIS = [
  {
    titulo: "Processos",
    descricao: "Informações gerais sobre os processos.",
    Icone: FolderCopyRounded,
    detalhes: ["Cadastrados", "Disponíveis para consulta"],
  },
  {
    titulo: "Documentos",
    descricao: "Informações gerais sobre os documentos.",
    Icone: DescriptionRounded,
    detalhes: ["Cadastrados", "Vinculados aos processos"],
  },
  {
    titulo: "Usuários",
    descricao: "Informações gerais sobre os usuários.",
    Icone: ManageAccounts,
    detalhes: ["Analistas", "Instituições"],
  },
];

// ====================================================
// COMPONENTE PRINCIPAL
// ====================================================

export default function PainelSuperintendencia() {
  const navigate = useNavigate();
  const reduzirMovimento = useReducedMotion();

  return (
    <Box sx={{ width: "100%", py: 0 }}>
      {/* ====================================================
          CABEÇALHO DO PAINEL
      ==================================================== */}

      <Box
        component={motion.div}
        {...obterTransicaoPaginaInicial("cabecalho", 0, reduzirMovimento)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: espacamentosGlobais.secoes,
          [TELA_COM_ALTURA_REDUZIDA]: {
            mb: 1,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: 1.5,
            color: "var(--sear-azul, #087cf0)",
            backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
          }}
        >
          <AccountBalanceRounded sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              color: coresTextosGlobais.tituloPagina,
              ...textosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              lineHeight: 1.15,
            }}
          >
            Painel da Superintendência
          </Typography>

          <Typography
            sx={{
              mt: 0,
              color: "var(--sear-texto-secundario, #7b8798)",
              ...textosGlobais.textoPadrao,
              fontSize: tamanhosControlesGlobais.descricao,
            }}
          >
            Acesse rapidamente as principais funcionalidades da
            Superintendência.
          </Typography>
        </Box>
      </Box>

      {/* ====================================================
          CARTÕES DE ACESSO
      ==================================================== */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: espacamentosGlobais.secoes,
          [TELA_COM_ALTURA_REDUZIDA]: {
            gap: 1,
          },
        }}
      >
        {ATALHOS.map(({ titulo, descricao, rota, Icone }, indice) => (
          <Paper
            key={titulo}
            component={motion.div}
            {...obterTransicaoPaginaInicial("card", indice, reduzirMovimento)}
            sx={{
              p: 2,
              minWidth: 0,
              border: "1px solid var(--sear-borda, #c9def4)",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",

              [TELA_COM_ALTURA_REDUZIDA]: {
                p: 1,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.4,
                flexGrow: 1,
              }}
            >
              <Box sx={ESTILO_CAIXA_ICONE}>
                <Icone
                  sx={{
                    fontSize: iconesGlobais.secao.fontSize,
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography component="h2" sx={ESTILO_TITULO_SECAO}>
                  {titulo}
                </Typography>

                <Typography
                  sx={{
                    ...ESTILO_DESCRICAO,
                    mt: espacamentosGlobais.tituloDescricao,
                    overflowWrap: "anywhere",
                  }}
                >
                  {descricao}
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowForwardRounded />}
              onClick={() => navigate(rota)}
              sx={{
                textTransform: "none",
                fontSize: tamanhosControlesGlobais.botao,
                mt: espacamentosGlobais.descricaoCampos,
                px: 4,
                backgroundColor: "var(--sear-acao-azul, #087cf0)",

                [TELA_COM_ALTURA_REDUZIDA]: {
                  mt: 1.5,
                },

                "&:hover": {
                  backgroundColor: "var(--sear-acao-azul-hover, #0767d8)",
                },

                "& .MuiButton-endIcon": {
                  position: "absolute",
                  right: 12,
                  margin: 0,
                },
              }}
            >
              Acessar
            </Button>
          </Paper>
        ))}
      </Box>

      {/* ====================================================
          VISÃO GERAL
          Sem consultas ao backend nesta etapa.
      ==================================================== */}

      <Paper
        component={motion.section}
        {...obterTransicaoPaginaInicial(
          "card",
          ATALHOS.length,
          reduzirMovimento,
        )}
        aria-labelledby="titulo-visao-geral-sre"
        aria-describedby="status-visao-geral-sre"
        elevation={0}
        sx={{
          mt: espacamentosGlobais.secoes,
          p: 2,
          border: "1px solid var(--sear-borda, #dcecff)",
          borderRadius: 2,
          backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",

          [TELA_COM_ALTURA_REDUZIDA]: {
            mt: 1.5,
            p: 1.5,
          },
        }}
      >
        {/* TÍTULO DA SEÇÃO */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            mb: espacamentosGlobais.descricaoCampos,

            [TELA_COM_ALTURA_REDUZIDA]: {
              mb: 1,
            },
          }}
        >
          <Typography
            id="titulo-visao-geral-sre"
            component="h2"
            sx={ESTILO_TITULO_SECAO}
          >
            Visão geral
          </Typography>

          <Box
            sx={{
              flex: 1,
              height: 1,
              backgroundColor: "var(--sear-superficie-hover, #a8d0ff)",
            }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 1.5,
          }}
        >
          {/* QUADROS VISUAIS SEM DADOS REAIS */}

          <Box
            aria-hidden="true"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: espacamentosGlobais.secoes,
              opacity: 0.45,
              filter: "blur(1px)",
              pointerEvents: "none",
              userSelect: "none",

              [TELA_COM_ALTURA_REDUZIDA]: {
                position: "absolute",
                inset: 0,
              },
            }}
          >
            {RESUMOS_VISUAIS.map(({ titulo, descricao, Icone, detalhes }) => (
              <Paper
                key={titulo}
                elevation={0}
                sx={{
                  p: 2,
                  minWidth: 0,
                  border: "1px solid var(--sear-borda, #dcecff)",
                  borderRadius: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.4,
                  }}
                >
                  <Box sx={ESTILO_CAIXA_ICONE}>
                    <Icone
                      sx={{
                        fontSize: iconesGlobais.secao.fontSize,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography sx={ESTILO_TITULO_SECAO}>{titulo}</Typography>

                    <Typography
                      sx={{
                        ...ESTILO_DESCRICAO,
                        mt: espacamentosGlobais.tituloDescricao,
                      }}
                    >
                      {descricao}
                    </Typography>
                  </Box>

                  <Typography
                    component="span"
                    sx={{
                      ...ESTILO_TITULO_SECAO,
                      px: 1.5,
                      borderRadius: 5,
                      backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
                      flexShrink: 0,
                    }}
                  >
                    —
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: espacamentosGlobais.descricaoCampos,
                    pt: 1,
                    borderTop: "1px solid var(--sear-borda, #e5edf5)",
                    display: "grid",
                    gap: espacamentosGlobais.tituloDescricao,
                  }}
                >
                  {detalhes.map((detalhe) => (
                    <Box
                      key={detalhe}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: "var(--sear-acao-azul, #087cf0)",
                        }}
                      />

                      <Typography
                        sx={{
                          ...ESTILO_DESCRICAO,
                          flex: 1,
                        }}
                      >
                        {detalhe}
                      </Typography>

                      <Typography sx={ESTILO_DESCRICAO}>—</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>

          {/* INDICADORES EM DESENVOLVIMENTO */}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              backgroundColor: "var(--sear-superficie, rgba(255, 255, 255, 0.68))",

              [TELA_COM_ALTURA_REDUZIDA]: {
                position: "relative",
                p: 1.5,
              },
            }}
          >
            <Box
              id="status-visao-geral-sre"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.4,
                maxWidth: "100%",
                p: 2,
                border: "1px solid var(--sear-borda, #c9def4)",
                borderRadius: 2,
                backgroundColor: "var(--sear-superficie, #ffffff)",
                boxShadow: "0 4px 14px rgba(8, 124, 240, 0.08)",

                [TELA_COM_ALTURA_REDUZIDA]: {
                  p: 1.5,
                },
              }}
            >
              <LockRounded
                sx={{
                  color: "var(--sear-azul, #087cf0)",
                  fontSize: iconesGlobais.secao.fontSize,
                  flexShrink: 0,
                }}
              />

              <Box>
                <Typography component="h3" sx={ESTILO_TITULO_SECAO}>
                  Em desenvolvimento
                </Typography>

                <Typography
                  sx={{
                    ...ESTILO_DESCRICAO,
                    mt: espacamentosGlobais.tituloDescricao,
                  }}
                >
                  Os indicadores serão disponibilizados em uma próxima
                  atualização.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
